import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Testimonials from '../home/components/Testimonials';
import ServiceAreas from '../home/components/ServiceAreas';
import ProcessSection from '../home/components/ProcessSection';
import WhyChooseUs from '../home/components/WhyChooseUs';
import { homeFaqs } from '../../mocks/faqs';

const projectTypes = [
  'Paver Driveway', 'Paver Patio', 'Pool Deck', 'Walkway / Pathway',
  'Retaining Wall', 'Outdoor Living Space', 'Hardscape Design', 'Other / Not Sure',
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', project_type: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d6va69e7ipaugi04lhv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(form as Record<string, string>).toString(),
      });
      if (res.ok) { setStatus('success'); setForm({ name: '', phone: '', email: '', project_type: '', message: '' }); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* 1. HERO */}
        <section className="relative min-h-[60vh] flex items-end pb-20 bg-parkside-darker"
          style={{ backgroundImage: "url('https://www.parksidepavers.com/files/projects/14/images/thumb/thumb_large_IMG%5F2835%2Ejpg')", backgroundSize: 'cover', backgroundPosition: 'center top' }}>
          <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker/92 via-parkside-darker/50 to-parkside-darker/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-parkside-darker/60 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="flex flex-wrap gap-2 mb-5">
              {['✔ Free Estimates', '✔ Responds Within 24 Hours', '✔ ICPI Certified'].map((b) => (
                <span key={b} className="bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3.5 py-1.5 rounded-full border border-white/20">{b}</span>
              ))}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[68px] text-white max-w-3xl mb-5 leading-tight tracking-display">
              Get Your Free Paver Estimate
            </h1>
            <p className="text-white/75 text-lg max-w-xl mb-8 leading-relaxed">
              Tell us about your project. We&apos;ll schedule a free on-site consultation and deliver a detailed estimate — no pressure, no commitment.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#estimate-form" className="bg-parkside-amber text-white font-bold px-8 py-4 rounded-xl hover:bg-parkside-amber/90 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Request Free Estimate
                <span className="w-5 h-5 inline-flex items-center justify-center"><i className="ri-arrow-down-line" /></span>
              </a>
              <a href="tel:813-404-6313" className="bg-white/12 backdrop-blur-sm border border-white/30 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/22 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <span className="w-5 h-5 inline-flex items-center justify-center"><i className="ri-phone-fill" /></span>
                (813) 404-6313
              </a>
            </div>
          </div>
        </section>

        {/* 2. TRUST BAR */}
        <section className="bg-white border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: 'ri-star-fill', label: '5-Star Rated on Google & Yelp', color: 'text-parkside-amber' },
                { icon: 'ri-map-pin-2-fill', label: 'Serving All of Tampa Bay', color: 'text-parkside-green' },
                { icon: 'ri-award-fill', label: 'ICPI Certified Installer', color: 'text-parkside-amber' },
                { icon: 'ri-shield-check-fill', label: 'Licensed, Bonded & Insured', color: 'text-parkside-green' },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-cream rounded-lg flex-shrink-0">
                    <i className={`${t.icon} ${t.color} text-base`} />
                  </div>
                  <span className="font-semibold text-parkside-dark text-sm">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. CONTACT FORM + INFO */}
        <section id="estimate-form" className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
              {/* Left: Info */}
              <div>
                <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Contact Us</span>
                <h2 className="font-display text-4xl md:text-5xl text-parkside-dark mb-5 tracking-display leading-tight">Reach Out Directly</h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-8">Ready to transform your outdoor space? Call us directly or fill out the form and we&apos;ll be in touch within 24 hours to schedule your free on-site consultation.</p>

                <div className="space-y-5 mb-10">
                  <a href="tel:813-404-6313" className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-14 h-14 flex items-center justify-center bg-parkside-green/12 rounded-2xl border border-parkside-green/20 group-hover:bg-parkside-green/20 transition-colors flex-shrink-0">
                      <i className="ri-phone-fill text-parkside-green text-xl" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-parkside-stone mb-0.5">Phone — Fastest Response</p>
                      <p className="text-parkside-dark font-bold text-xl group-hover:text-parkside-green transition-colors">(813) 404-6313</p>
                      <p className="text-parkside-stone text-sm">Mon–Sat, 8am–6pm</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-parkside-amber/12 rounded-2xl border border-parkside-amber/20 flex-shrink-0">
                      <i className="ri-map-pin-fill text-parkside-amber text-xl" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-parkside-stone mb-0.5">Service Area</p>
                      <p className="text-parkside-dark font-bold text-base">Tampa Bay & Surrounding Areas</p>
                      <p className="text-parkside-stone text-sm">Tampa · St. Pete · Clearwater · Tarpon Springs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-parkside-green/12 rounded-2xl border border-parkside-green/20 flex-shrink-0">
                      <i className="ri-time-fill text-parkside-green text-xl" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-parkside-stone mb-0.5">Response Time</p>
                      <p className="text-parkside-dark font-bold text-base">Within 24 Hours</p>
                      <p className="text-parkside-stone text-sm">We typically respond same day</p>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="rounded-2xl overflow-hidden border border-stone-200 h-64 relative">
                  <iframe
                    title="Parkside Pavers Tampa Bay Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d112946.68891040516!2d-82.53090219999999!3d27.994402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c2b782b3b9d1e1%3A0xa75f605293570f50!2sTampa%2C%20FL!5e0!3m2!1sen!2sus!4v1699000000000!5m2!1sen!2sus"
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="absolute top-3 left-3 bg-white rounded-xl px-3 py-2 border border-stone-100">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center bg-parkside-green rounded-lg"><i className="ri-map-pin-fill text-white text-xs" /></div>
                      <div><p className="text-parkside-dark font-bold text-xs">Parkside Pavers</p><p className="text-parkside-stone text-[10px]">Tampa Bay, FL</p></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white rounded-2xl p-8 border border-stone-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 flex items-center justify-center bg-parkside-amber/12 rounded-xl">
                    <i className="ri-file-list-3-line text-parkside-amber text-xl" />
                  </div>
                  <div>
                    <h3 className="text-parkside-dark font-bold text-xl leading-tight">Request Your Free Estimate</h3>
                    <p className="text-parkside-stone text-xs mt-0.5">No commitment — we&apos;ll respond within 24 hours.</p>
                  </div>
                </div>

                {status === 'success' ? (
                  <div className="text-center py-14">
                    <div className="w-20 h-20 bg-parkside-green/10 rounded-full flex items-center justify-center mx-auto mb-5">
                      <i className="ri-checkbox-circle-fill text-parkside-green text-4xl" />
                    </div>
                    <p className="text-parkside-dark font-bold text-2xl mb-2">Request Sent!</p>
                    <p className="text-parkside-stone text-sm">Thank you! We&apos;ll be in touch within 24 hours to schedule your free consultation.</p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Full Name *</label>
                        <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith"
                          className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all" />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Phone Number *</label>
                        <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="(813) 000-0000"
                          className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@email.com"
                        className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Project Type</label>
                      <select name="project_type" value={form.project_type} onChange={handleChange}
                        className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all cursor-pointer bg-white">
                        <option value="">Select a project type...</option>
                        {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Message</label>
                      <textarea name="message" value={form.message} onChange={handleChange} rows={4} maxLength={500}
                        placeholder="Tell us about your project, property size, timeline..."
                        className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all resize-none" />
                      <p className="text-xs text-parkside-stone mt-1 text-right">{form.message.length}/500</p>
                    </div>
                    {status === 'error' && <p className="text-red-500 text-xs">Something went wrong. Please call us at (813) 404-6313.</p>}
                    <button type="submit" disabled={status === 'sending'}
                      className="w-full bg-parkside-amber text-white font-bold py-4 rounded-xl hover:bg-parkside-amber/90 transition-all disabled:opacity-70 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                      {status === 'sending' ? <><i className="ri-loader-4-line animate-spin" /> Sending...</> : <>Request Free Estimate — No Commitment <i className="ri-arrow-right-line" /></>}
                    </button>
                    <div className="flex items-center justify-center gap-4 pt-1">
                      {[{ icon: 'ri-shield-check-line', c: 'text-parkside-green', t: 'Licensed & Insured' }, { icon: 'ri-star-fill', c: 'text-parkside-amber', t: '5-Star Rated' }, { icon: 'ri-award-line', c: 'text-parkside-green', t: 'ICPI Certified' }].map((b) => (
                        <div key={b.t} className="flex items-center gap-1.5 text-xs text-parkside-stone">
                          <span className="w-3 h-3 inline-flex items-center justify-center"><i className={`${b.icon} ${b.c}`} /></span>
                          {b.t}
                        </div>
                      ))}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 4. AI ANSWER BLOCK */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-parkside-cream rounded-2xl p-8 md:p-10 border border-stone-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-amber/12 rounded-lg">
                    <i className="ri-robot-line text-parkside-amber text-lg" />
                  </div>
                  <span className="text-parkside-amber text-xs font-bold uppercase tracking-widest">AI Answer Block</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-parkside-dark mb-5 tracking-display leading-tight">Get a Free Paver Estimate in Tampa Bay</h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-4">
                  Parkside Pavers provides free on-site consultations and detailed estimates for all paver projects throughout Tampa Bay. Services include custom patios, driveway paver systems, pool decks, retaining walls, and outdoor living spaces.
                </p>
                <p className="text-parkside-muted text-base leading-relaxed">
                  As an ICPI-certified installer, Parkside Pavers follows the highest industry standards. All estimates are transparent, detailed, and delivered with no pressure or commitment required.
                </p>
              </div>
              <div className="bg-parkside-darker rounded-2xl p-8 md:p-10">
                <h3 className="text-white font-bold text-xl mb-5">What to Expect</h3>
                <ul className="space-y-3.5">
                  {['Free on-site consultation at your property', 'Detailed estimate — no hidden fees', 'Custom design recommendation', 'Material selection guidance', 'Clear project timeline', 'Response within 24 hours'].map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm text-white/70">
                      <div className="w-6 h-6 flex items-center justify-center bg-parkside-amber/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-amber text-sm" />
                      </div>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5. PROCESS */}
        <ProcessSection />

        {/* 6. WHY CHOOSE US */}
        <WhyChooseUs />

        {/* 7. SERVICE AREAS */}
        <ServiceAreas />

        {/* 8. TESTIMONIALS */}
        <Testimonials />

        {/* 9. FAQ */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-2">
                <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Voice Search FAQs</span>
                <h2 className="font-display text-4xl text-parkside-dark mb-5 tracking-display leading-tight">Common Questions</h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-5">Everything you need to know before contacting us.</p>
                <div className="bg-white rounded-2xl p-5 border border-stone-100">
                  <a href="tel:813-404-6313" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center bg-parkside-green/15 rounded-xl flex-shrink-0">
                      <i className="ri-phone-fill text-parkside-green text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-parkside-stone font-semibold uppercase tracking-wider">Prefer to Call?</p>
                      <p className="text-parkside-dark font-bold group-hover:text-parkside-green transition-colors">(813) 404-6313</p>
                    </div>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-2">
                {homeFaqs.map((faq, idx) => (
                  <div key={idx} className={`rounded-2xl border overflow-hidden transition-all ${openFaq === idx ? 'border-parkside-green/30 bg-white' : 'border-stone-100 bg-white'}`}>
                    <button className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-colors ${openFaq === idx ? 'bg-parkside-green text-white' : 'bg-parkside-cream text-parkside-muted'}`}>
                          <i className="ri-question-line text-xs" />
                        </div>
                        <span className="text-parkside-dark font-semibold text-sm leading-snug">{faq.question}</span>
                      </div>
                      <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-parkside-cream">
                        <i className={`text-parkside-green text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </span>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6">
                        <div className="ml-9 border-l-2 border-parkside-green/30 pl-4">
                          <p className="text-parkside-muted text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 10. FINAL CTA */}
        <section className="py-16 bg-parkside-green">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Limited Availability</span>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-4 tracking-display leading-tight">Don&apos;t Wait — Spring Spots Are Filling Fast</h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">Parkside Pavers is currently scheduling 2–3 weeks out. Reserve your free estimate today.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#estimate-form" className="bg-white text-parkside-green font-bold px-8 py-4 rounded-xl hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                Request Free Estimate <i className="ri-arrow-up-line" />
              </a>
              <a href="tel:813-404-6313" className="border-2 border-white/40 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-phone-fill" /> Call Now
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
